import React, { useEffect, useState } from 'react';

import { Box, Button, Col, Row, Text, TextInput } from '@opendoor/bricks-next';
import { analytics } from '@opendoor/observability/slim';

import { useObservability } from '../../helpers/observability';
import * as api from '../api';
import { PARTNERSHIP_BFF_URL } from '../globals';

const valuePropItems = [
  {
    header: 'Streamline your process',
    description:
      'Create a custom profile to track and manage every touch point of your customer’s journey.',
  },
  {
    header: 'Help your client get started',
    description:
      'Help your client get started. Use the Home Eligibility Checker to confirm your client’s home is eligible.',
  },
  {
    header: 'Sell more homes, faster',
    description:
      'Get your clients a competitive estimated offer within minutes — all with a few simple clicks.',
  },
  {
    header: 'Access dedicated support',
    description:
      'Access your own partner manager, training, insights, and more to help you sell more homes.',
  },
];

const BuilderSalesPage: React.FC = () => {
  const { trackEvent } = useObservability();
  const [email, setEmail] = useState('');
  const [ctaInputLoading, setCtaInputLoading] = useState(false);
  const [isEmailInvalid, setIsEmailInvalid] = useState<boolean>(false);
  const [emailFormatError, setEmailFormatError] = useState(false);

  useEffect(() => {
    analytics.trackEvent('partner-sign-up-flow', 'view-landing-page');
  }, []);

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSubmit = async () => {
    if (!email) return;
    if (!isValidEmail(email)) {
      setEmailFormatError(true);
      return;
    }
    setCtaInputLoading(true);
    try {
      const resp = await api.validatePartnerEmailAddress(email);
      if (!resp || !resp.validateEmail.isValid) {
        setIsEmailInvalid(true);
        setCtaInputLoading(false);
        trackEvent(
          'invalid-email',
          undefined,
          { email },
          { categoryOverride: 'partner-sign-up-flow' },
        );
        return;
      }
      trackEvent('valid-email', undefined, undefined, {
        categoryOverride: 'partner-sign-up-flow',
      });
      const normalizedEmail = resp.validateEmail.normalizedEmail;
      const redirectParams = new URLSearchParams();
      redirectParams.set('email', normalizedEmail);
      redirectParams.set('partnershipType', 'HOME_BUILDER');
      redirectParams.set('role', 'partner_sales_referrer');
      window.location.assign(`${PARTNERSHIP_BFF_URL}/sign-up?${redirectParams.toString()}`);
    } catch {
      setCtaInputLoading(false);
    }
  };

  return (
    <Col
      p="$16"
      gap="$2"
      backgroundColor="$backgroundAccentBrand"
      $largerThanMD={{ height: '$150' }}
    >
      <Col alignItems="flex-start" mx="auto" $largerThanXS={{ px: '$0' }} p="$2" maxWidth="$232">
        <Col gap="$4">
          <Text
            tag="h1"
            typography="$headerSmall"
            color="$contentInversePrimary"
            textAlign="left"
            maxWidth="100%"
            $largerThanSM={{ maxWidth: '$150' }}
          >
            Sign up for your customized dashboard today
          </Text>
          <Col width="100%" gap="$1">
            <Box gap="$2" alignItems="flex-start" $largerThanSM={{ flexDirection: 'row' }}>
              <Col flex={1} width="100%">
                <TextInput
                  size="medium"
                  ariaLabel="Enter your work email"
                  placeholder="Enter your work email"
                  value={email}
                  onChangeText={(value) => {
                    setEmail(value);
                    setEmailFormatError(false);
                    setIsEmailInvalid(false);
                  }}
                  inputMode="email"
                  autoComplete="email"
                />
              </Col>
              <Button
                variant="secondaryInverse"
                size="medium"
                label="Get started"
                aria-label="Get started"
                loading={ctaInputLoading}
                onPress={handleSubmit}
                analyticsName="cosmos-builder-sales-page-cta-button"
              />
            </Box>
            {emailFormatError && (
              <Text
                tag="p"
                typography="$labelSmall"
                color="$contentInversePrimary"
                textAlign="left"
              >
                Please enter a valid email address.
              </Text>
            )}
            {isEmailInvalid && (
              <Text
                tag="p"
                backgroundColor="$backgroundStateInactiveAccentError"
                typography="$labelSmall"
                pl="$2"
                py="$2"
                color="$contentInversePrimary"
                textAlign="left"
                borderRadius="$4x"
              >
                Please use an email address associated with your builder to continue.
              </Text>
            )}
          </Col>
        </Col>

        <Row mt="$22" flexWrap="wrap" width="100%" gap="$4" $largerThanSM={{ flexWrap: 'nowrap' }}>
          {valuePropItems.map((valueProp, i) => (
            <Col key={i} width="100%" $largerThanSM={{ width: '25%' }} gap="$2">
              <Text tag="h4" typography="$subheaderXsmall" color="$contentInversePrimary">
                {valueProp.header}
              </Text>
              <Text tag="p" color="$contentInversePrimary" typography="$bodySmall" width="85%">
                {valueProp.description}
              </Text>
            </Col>
          ))}
        </Row>
      </Col>
    </Col>
  );
};

export default BuilderSalesPage;
